import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CalendarCheck, CheckCircle2, Clock, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";
import { whatsappLink } from "@/constants/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIME_SLOTS = ["09:00", "10:00", "11:00", "14:00", "15:00", "16:00", "17:00"];

const inputClass = "border-emerald-500/20 bg-black/40 text-slate-100 placeholder:text-slate-600 focus-visible:ring-emerald-500";

export default function Booking({ preselectedService }) {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ service: "", date: "", time: "", name: "", email: "", phone: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (preselectedService) setForm((f) => ({ ...f, service: preselectedService }));
  }, [preselectedService]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const minDate = new Date().toISOString().split("T")[0];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service || !form.date || !form.time || !form.name || !form.email || !form.phone) {
      toast.error(t("booking.errorRequired"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setSuccess(true);
      toast.success(t("booking.successMsg"));
    } catch (err) {
      toast.error(t("booking.errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  const waMsg = lang === "pt"
    ? "Olá! Gostaria de agendar uma reunião online para falar sobre soluções em tecnologia."
    : "Hello! I would like to schedule an online meeting to discuss technology solutions.";

  return (
    <section id="agendar" data-testid="booking-section" className="relative py-20 sm:py-28">
      <div className="pointer-events-none absolute bottom-0 left-1/2 h-[400px] w-[800px] -translate-x-1/2 rounded-full bg-emerald-500/8 blur-[140px]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-start gap-12 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <span className="eyebrow">{t("booking.eyebrow")}</span>
            <h2 className="font-display mt-4 text-3xl font-normal tracking-tight text-slate-50 sm:text-4xl">
              {t("booking.title")}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-400">{t("booking.sub")}</p>
            <div className="mt-8 flex items-center gap-3 rounded-xl border border-emerald-500/15 bg-black/30 px-5 py-4">
              <Clock className="h-5 w-5 shrink-0 text-emerald-400" />
              <span className="text-sm text-slate-300">{t("booking.responseTime")}</span>
            </div>
            <a data-testid="booking-whatsapp-cta" href={whatsappLink(waMsg)} target="_blank" rel="noopener noreferrer"
              className="glow-button mt-6 inline-flex items-center gap-2.5 rounded-full border border-emerald-400/40 bg-emerald-500/10 px-6 py-3 text-sm font-semibold text-emerald-300 hover:bg-emerald-500/20">
              <MessageCircle className="h-4.5 w-4.5" />
              {t("booking.whatsappCta")}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7"
          >
            <div className="shimmer-border rounded-2xl bg-[#08100d]/80 p-7 backdrop-blur-xl sm:p-9">
              {success ? (
                <div data-testid="booking-confirmation-banner" className="flex flex-col items-center py-10 text-center">
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 14 }}>
                    <CheckCircle2 className="h-16 w-16 text-emerald-400" />
                  </motion.div>
                  <h3 className="font-display mt-6 text-2xl text-slate-50">{t("booking.successTitle")}</h3>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-400">{t("booking.successMsg")}</p>
                  <Button
                    data-testid="booking-new-button"
                    onClick={() => { setSuccess(false); setForm({ service: "", date: "", time: "", name: "", email: "", phone: "", notes: "" }); }}
                    variant="outline"
                    className="mt-8 border-emerald-500/30 bg-transparent text-emerald-300 hover:bg-emerald-500/10"
                  >
                    {t("booking.newBooking")}
                  </Button>
                </div>
              ) : (
                <form data-testid="booking-form-element" onSubmit={submit} className="space-y-5">
                  <div className="grid gap-5 sm:grid-cols-3">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-slate-400">{t("booking.service")}</Label>
                      <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                        <SelectTrigger data-testid="booking-service-dropdown" className={inputClass}>
                          <SelectValue placeholder={t("booking.servicePlaceholder")} />
                        </SelectTrigger>
                        <SelectContent className="border-emerald-500/20 bg-[#08100d] text-slate-100">
                          {t("services.cards").map((s) => (
                            <SelectItem key={s.id} value={s.id} data-testid={`booking-service-option-${s.id}`}>{s.title}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-slate-400">{t("booking.date")}</Label>
                      <Input data-testid="booking-date-picker" type="date" min={minDate} value={form.date} onChange={set("date")} className={inputClass} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-slate-400">{t("booking.time")}</Label>
                      <Select value={form.time} onValueChange={(v) => setForm((f) => ({ ...f, time: v }))}>
                        <SelectTrigger data-testid="booking-time-slot" className={inputClass}>
                          <SelectValue placeholder={t("booking.timePlaceholder")} />
                        </SelectTrigger>
                        <SelectContent className="border-emerald-500/20 bg-[#08100d] text-slate-100">
                          {TIME_SLOTS.map((slot) => (
                            <SelectItem key={slot} value={slot} data-testid={`booking-time-option-${slot.replace(":", "")}`}>{slot}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-slate-400">{t("booking.name")}</Label>
                      <Input data-testid="booking-input-name" value={form.name} onChange={set("name")} className={inputClass} placeholder="Ada Lovelace" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-slate-400">{t("booking.phone")}</Label>
                      <Input data-testid="booking-input-phone" value={form.phone} onChange={set("phone")} className={inputClass} placeholder="+55 11 99999-0000" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-slate-400">{t("booking.email")}</Label>
                    <Input data-testid="booking-input-email" type="email" value={form.email} onChange={set("email")} className={inputClass} placeholder="voce@empresa.com" />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-slate-400">{t("booking.notes")}</Label>
                    <Textarea data-testid="booking-input-notes" value={form.notes} onChange={set("notes")} rows={3} className={inputClass} />
                  </div>

                  <Button
                    data-testid="booking-submit-button"
                    type="submit"
                    disabled={loading}
                    className="glow-button w-full gap-2.5 border border-emerald-400/50 bg-emerald-500 py-6 text-sm font-semibold text-[#030706] hover:bg-emerald-400"
                  >
                    <CalendarCheck className="h-4.5 w-4.5" />
                    {loading ? t("booking.submitting") : t("booking.submit")}
                  </Button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
